import { NO_PROXY } from '@hit-game/shared/constants';
import { AccountRunStatus, AccountSide, BET_KEYS_BY_GAME, GameType } from '@hit-game/shared/enums';
import type { GameBetKey } from '@hit-game/shared/enums';
import { SIDE_NAMES, minCoinOf } from '@hit-game/shared/constants';
import type { IAccountDataResponse, IGroup, IProxyListItem } from '@hit-game/shared/types';
import { AlertTriangle, Check, Info, Loader2, RefreshCw, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useToast } from '../../context/ToastContext';
import { fetchAllPages } from '../../lib/http';
import { accountService } from '../../services/account.service';
import { proxyService } from '../../services/proxy.service';
import { Button } from '../common/Button';
import { Dropdown, type DropdownOption } from '../common/Dropdown';
import { FIELD_CLASS, LABEL_CLASS } from '../common/form.styles';
import { Modal } from '../common/Modal';
import { GAME_OPTIONS, MEMBER_COLORS } from './group.constants';
import { SegmentTab, SideDoorGrid } from './GroupConfigFields';
import { ProxyCheckButton, type ProxyHealth } from './GroupProxyPicker';
import {
  emptyGrid,
  toDraft,
  toGrid,
  toMembers,
  toSideConfigs,
  warnDrafts,
  type DoorGrid,
  type DraftMember,
} from './group.form';

/** Rộng hơn hộp thoại tạo — bốn thẻ tài khoản xếp hai cột. */
const MODAL_WIDTH = 900;

interface GroupConfigModalProps {
  /** `null` = đóng. Mở lại với nhóm khác thì form nạp lại từ đầu. */
  group: IGroup | null;
  accounts: IAccountDataResponse[];
  onClose: () => void;
  onSubmit: (group: IGroup, input: Partial<IGroup>) => Promise<boolean>;
}

/**
 * Sửa nhóm: tên, game, và phe + mệnh giá của TỪNG thành viên.
 *
 * Thêm/bớt thành viên vẫn nằm ở bảng ngoài trang — ở đó mới nhìn được trạng thái
 * đăng nhập và proxy để biết nên kéo tài khoản nào vào. Còn phe và mệnh giá thì
 * ngược lại: phải xem cả nhóm cạnh nhau mới đặt cân được, nên gom vào đây.
 */
export function GroupConfigModal({ group, accounts, onClose, onSubmit }: GroupConfigModalProps) {
  const toast = useToast();

  const [name, setName] = useState('');
  const [gameType, setGameType] = useState<GameType>(GameType.TX);
  const [drafts, setDrafts] = useState<DraftMember[]>([]);
  const [grid, setGrid] = useState<DoorGrid>(emptyGrid);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** Kho proxy để mời chọn — proxy thuộc về Account nên phải tự tải, không có sẵn trong `group`. */
  const [proxies, setProxies] = useState<IProxyListItem[]>([]);
  /** Proxy đang chọn cho từng thành viên, khoá theo `accountId`. */
  const [proxyDraft, setProxyDraft] = useState<Record<string, string>>({});
  const [proxyHealth, setProxyHealth] = useState<Record<string, ProxyHealth>>({});

  // Nạp lại mỗi lần mở: mở popup của nhóm khác mà giữ state cũ là sửa nhầm nhóm.
  useEffect(() => {
    if (!group) return;
    setName(group.name);
    setGameType(group.gameType);
    setDrafts(toDraft(group.members));
    setGrid(toGrid(group.sideConfigs, group.gameType));
    setError(null);
    setProxyHealth({});

    const byId = new Map(accounts.map((item) => [String(item._id), item]));
    setProxyDraft(
      Object.fromEntries(
        group.members.map((member) => [
          String(member.accountId),
          byId.get(String(member.accountId))?.proxy ?? NO_PROXY,
        ]),
      ),
    );
    // `accounts` cố tình KHÔNG nằm trong deps: trang tự tải lại kho tài khoản
    // theo nhịp riêng, chạy lại effect theo nó là proxy đang chọn dở bị bật về
    // giá trị dưới server giữa chừng.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  // Kho proxy đọc một lần mỗi lần mở — mở popup mới có thể proxy đã đổi chủ.
  useEffect(() => {
    if (!group) return;

    const load = async () => {
      try {
        setProxies(await fetchAllPages((params) => proxyService.list(params)));
      } catch {
        setProxies([]);
      }
    };
    void load();
  }, [group]);

  /** Proxy còn rảnh + proxy của chính tài khoản này, trừ những cái thành viên khác vừa chọn. */
  const proxyOptionsFor = (accountId: string) => {
    const pickedByOthers = new Set(
      Object.entries(proxyDraft)
        .filter(([id, value]) => id !== accountId && value !== NO_PROXY)
        .map(([, value]) => value),
    );
    const current = proxyDraft[accountId] ?? NO_PROXY;

    // Chú kiểu tường minh: `NO_PROXY` là literal `as const`, để suy kiểu thì mảng
    // bị thu hẹp về đúng chuỗi đó và không nhận nổi giá trị proxy nào khác.
    const options: DropdownOption[] = [{ value: NO_PROXY, label: NO_PROXY }];
    for (const proxy of proxies) {
      const free = !proxy.accountId || String(proxy.accountId) === accountId;
      if (!free || pickedByOthers.has(proxy.value)) continue;
      options.push({ value: proxy.value, label: proxy.value });
    }

    // Proxy đang gán mà không còn trong kho (vừa bị xoá) vẫn phải hiện, nếu không
    // ô chọn trông như đang để trống và lưu là gỡ mất proxy ngoài ý muốn.
    if (current !== NO_PROXY && !options.some((option) => option.value === current)) {
      options.push({ value: current, label: current });
    }
    return options;
  };

  const checkProxy = async (accountId: string) => {
    const value = proxyDraft[accountId];
    if (!value || value === NO_PROXY) return;

    setProxyHealth((prev) => ({ ...prev, [accountId]: 'checking' }));
    try {
      const [result] = await proxyService.checkHealth([value]);
      // `unknown` = lỗi ở phía máy chủ check, KHÔNG phải proxy chết — gộp vào
      // "chết" là bảo người dùng đổi proxy đang chạy tốt.
      if (result?.status === 'unknown') {
        setProxyHealth((prev) => ({ ...prev, [accountId]: 'idle' }));
        toast.info('Chưa kết luận được — lỗi từ phía máy chủ, thử lại sau');
        return;
      }
      setProxyHealth((prev) => ({ ...prev, [accountId]: result?.alive ? 'alive' : 'dead' }));
    } catch {
      setProxyHealth((prev) => ({ ...prev, [accountId]: 'idle' }));
      toast.error('Không kiểm tra được proxy');
    }
  };

  /**
   * Gán lại proxy cho những tài khoản THỰC SỰ đổi.
   *
   * Proxy nằm trên Account chứ không nằm trong nhóm, nên phải đi bằng request
   * riêng cho từng tài khoản. Trả về danh sách tài khoản gán hỏng để báo lại —
   * nuốt lỗi ở đây là người dùng tưởng đã đổi xong.
   */
  const applyProxyChanges = async (): Promise<string[]> => {
    const failed: string[] = [];

    for (const draft of drafts) {
      const next = proxyDraft[draft.accountId] ?? NO_PROXY;
      const account = accounts.find((item) => String(item._id) === draft.accountId);
      const current = account?.proxy ?? NO_PROXY;
      if (next === current) continue;

      try {
        await accountService.assignProxy(draft.accountId, next === NO_PROXY ? null : next);
      } catch {
        failed.push(account?.username ?? draft.accountId);
      }
    }

    return failed;
  };

  const patch = (index: number, next: Partial<DraftMember>) => {
    setDrafts((prev) => prev.map((item, i) => (i === index ? { ...item, ...next } : item)));
  };

  const submit = async () => {
    if (!group) return;
    if (!name.trim()) {
      setError('Nhập tên nhóm');
      return;
    }

    setSaving(true);
    try {
      /**
       * Tự kiểm proxy khi bấm Lưu — proxy chết thì tài khoản không vào nổi phiên
       * nào, biết trước còn hơn ngồi chờ mãi không thấy lệnh nào đi.
       *
       * Bỏ qua tài khoản đang chạy (ô proxy của nó vốn đã khoá, kiểm lại chỉ
       * chặn việc lưu mệnh giá mới) và proxy vừa bấm Check thấy sống trong chính
       * lần mở này.
       */
      const needCheck = drafts.filter((draft) => {
        const value = proxyDraft[draft.accountId] ?? NO_PROXY;
        if (value === NO_PROXY) return false;
        if (proxyHealth[draft.accountId] === 'alive') return false;
        const account = accounts.find((item) => String(item._id) === draft.accountId);
        return account?.status !== AccountRunStatus.RUNNING;
      });

      if (needCheck.length > 0) {
        setProxyHealth((prev) => ({
          ...prev,
          ...Object.fromEntries(needCheck.map((draft) => [draft.accountId, 'checking' as const])),
        }));

        const values = [...new Set(needCheck.map((draft) => proxyDraft[draft.accountId] ?? ''))];
        const results = await proxyService.checkHealth(values).catch(() => []);
        const statusByValue = new Map(results.map((item) => [item.value, item.status]));

        setProxyHealth((prev) => {
          const next = { ...prev };
          for (const draft of needCheck) {
            const status = statusByValue.get(proxyDraft[draft.accountId] ?? '');
            // `unknown` = lỗi phía máy chủ check, không kết luận gì về proxy.
            if (status === 'alive') next[draft.accountId] = 'alive';
            else if (status === 'dead') next[draft.accountId] = 'dead';
            else next[draft.accountId] = 'idle';
          }
          return next;
        });

        if (
          needCheck.some((draft) => statusByValue.get(proxyDraft[draft.accountId] ?? '') === 'dead')
        ) {
          setError('Proxy không hoạt động — chọn proxy khác rồi lưu lại');
          return;
        }
      }

      // Gán proxy TRƯỚC khi lưu nhóm: `onSubmit` kéo lại dữ liệu trang ở bước
      // cuối, đảo thứ tự là bảng vẽ lại bằng proxy cũ.
      const failedProxy = await applyProxyChanges();

      const ok = await onSubmit(group, {
        name: name.trim(),
        gameType,
        members: toMembers(drafts),
        sideConfigs: toSideConfigs(grid, gameType),
      });

      if (!ok) {
        setError('Lưu không thành công — kiểm tra lại rồi thử lại');
        return;
      }

      if (failedProxy.length > 0) {
        toast.error(`Đã lưu nhóm nhưng không gán được proxy cho: ${failedProxy.join(', ')}`);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  /**
   * Nhóm đang chạy khi có thành viên đang RUNNING. Lúc đó chỉ sửa được TIỀN HAI PHE (áp dụng từ
   * ván sau) — tên và game bị runner giữ ảnh chụp từ lúc Start nên server từ chối, khoá sẵn ở đây
   * để thấy lý do trước khi bấm.
   */
  const groupRunning = drafts.some((draft) => {
    const account = accounts.find((item) => String(item._id) === draft.accountId);
    return account?.status === AccountRunStatus.RUNNING;
  });

  const sideConfigs = toSideConfigs(grid, gameType);
  const warnings = warnDrafts(drafts, gameType, sideConfigs);

  const setDoorAmount = (side: AccountSide, key: GameBetKey, amount: number) => {
    setGrid((prev) => ({ ...prev, [side]: { ...prev[side], [key]: amount } }));
  };

  return (
    <Modal
      open={group !== null}
      onClose={onClose}
      busy={saving}
      maxWidth={MODAL_WIDTH}
      title={
        <span className="flex items-center gap-2">
          Sửa nhóm:
          <span className="text-accent">{group?.name}</span>
        </span>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Huỷ
          </Button>
          <Button
            onClick={() => {
              void submit();
            }}
            loading={saving}
          >
            Lưu tất cả
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <label className={LABEL_CLASS} htmlFor="group-config-name">
              Tên nhóm
            </label>
            <input
              id="group-config-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="VD: Cặp Tài Xỉu 1"
              disabled={groupRunning}
              className={`${FIELD_CLASS} disabled:cursor-not-allowed disabled:opacity-50`}
            />
          </div>

          <div className="shrink-0">
            <span className={LABEL_CLASS}>Game</span>
            <SegmentTab
              options={GAME_OPTIONS}
              value={gameType}
              disabled={groupRunning}
              onChange={(value) => {
                // Cửa gắn với game: đổi game thì tiền của cửa game cũ không còn nghĩa nên lưới về 0.
                if (value !== gameType) setGrid(emptyGrid());
                setGameType(value as GameType);
              }}
            />
          </div>
        </div>

        {groupRunning && (
          <p className="border-line bg-canvas text-ink-dim flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[11px] leading-relaxed">
            <Info size={13} className="mt-0.5 shrink-0" />
            <span>
              Nhóm đang chạy — chỉ sửa được <b>tiền hai phe</b>, và <b>áp dụng từ ván sau</b> (ván
              đang đặt giữ nguyên số cũ). Muốn đổi tên, game hay thành viên thì bấm Stop trước.
            </span>
          </p>
        )}

        {/* Lưới tiền của cả NHÓM: hàng = cửa, cột = Phe A / Phe B. Mỗi ván engine
            random 2 tài khoản vào mỗi phe rồi tự cộng các cửa của phe và rải. */}
        <SideDoorGrid
          keys={BET_KEYS_BY_GAME[gameType]}
          grid={grid}
          minCoin={minCoinOf(gameType)}
          disabled={saving}
          onChange={setDoorAmount}
        />

        <p className="border-line bg-canvas text-ink-dim flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[11px] leading-relaxed">
          <Info size={13} className="mt-0.5 shrink-0" />
          <span>
            Mỗi ván, 2 trong 4 tài khoản được random vào <b>{SIDE_NAMES[AccountSide.CHAN]}</b>, 2
            vào <b>{SIDE_NAMES[AccountSide.LE]}</b>. Nhập tiền cho từng cửa, để trống = không đánh
            cửa đó; một cửa có thể có tiền ở cả hai phe.{' '}
            {gameType === GameType.BAUCUA ? (
              <>
                Tổng tiền của phe được chia <b>đều</b> cho 2 tài khoản (mỗi người một nửa)
              </>
            ) : (
              <>
                Tổng tiền của phe được chia <b>lệch</b> cho 2 tài khoản (người nhiều số dư hơn đánh
                nhiều hơn, khoảng 55–65%)
              </>
            )}{' '}
            rồi rải thành nhiều gói nhỏ theo đúng tỉ lệ các cửa. <b>Đổi game sẽ đặt lại lưới về 0.</b>
          </span>
        </p>

        {drafts.length === 0 ? (
          <div className="border-line text-ink-faint rounded-lg border border-dashed py-10 text-center text-[12px]">
            Nhóm chưa có tài khoản nào — thêm ở bảng ngoài trang Nhóm trước đã.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {drafts.map((draft, index) => {
              const account = accounts.find((item) => String(item._id) === draft.accountId);
              const running = account?.status === AccountRunStatus.RUNNING;

              return (
                <div
                  key={draft.accountId || index}
                  className="border-line bg-panel-raised flex flex-col gap-3 rounded-xl border p-4"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="truncate font-mono text-[13px] font-bold"
                      style={{ color: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
                      title={account?.username}
                    >
                      {account?.username ?? 'Tài khoản đã xoá'}
                    </span>
                    <span
                      className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                        running
                          ? 'border-warning/40 bg-warning/10 text-warning'
                          : 'border-success-border bg-success-bg text-success'
                      }`}
                    >
                      {running ? 'Đang chạy' : 'Đã dừng'}
                    </span>
                  </div>

                  <div>
                    <span className={LABEL_CLASS}>
                      Proxy
                      {running && (
                        <span className="text-warning ml-1.5 normal-case tracking-normal">
                          — khoá khi đang chạy, dừng ở Sảnh để đổi
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <Dropdown
                        className="min-w-0 flex-1"
                        value={proxyDraft[draft.accountId] ?? NO_PROXY}
                        onChange={(value) => {
                          setProxyDraft((prev) => ({ ...prev, [draft.accountId]: value }));
                          // Kết quả check thuộc về proxy CŨ, giữ lại là dán nhãn
                          // "sống" cho proxy chưa ai kiểm tra bao giờ.
                          setProxyHealth((prev) => ({ ...prev, [draft.accountId]: 'idle' }));
                        }}
                        // Phiên WebSocket đang chạy đã bám vào proxy cũ — đổi
                        // trong DB chỉ làm lệch giữa thứ hiện ra và thứ đang chạy.
                        disabled={running || saving}
                        options={proxyOptionsFor(draft.accountId)}
                        maxHeight={200}
                      />
                      <ProxyCheckButton
                        state={proxyHealth[draft.accountId] ?? 'idle'}
                        disabled={saving || (proxyDraft[draft.accountId] ?? NO_PROXY) === NO_PROXY}
                        onClick={() => {
                          void checkProxy(draft.accountId);
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-ink-dim flex items-center gap-1.5 text-[11px]">
          <Info size={12} className="shrink-0" />
          Thay đổi có hiệu lực từ phiên kế tiếp. Proxy được kiểm tra tự động khi bấm Lưu.
        </p>

        {error && <p className="text-danger text-[12px] font-medium">{error}</p>}
        {!error &&
          warnings.map((warning) => (
            <p
              key={warning}
              className="text-warning flex items-start gap-1.5 text-[11px] font-medium"
            >
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              {warning}
            </p>
          ))}
      </div>
    </Modal>
  );
}
